import { useState } from 'react'
import { Sparkles, Check, Download } from 'lucide-react'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import Badge from '../../components/ui/Badge'

export default function Subscription() {
  const [billing, setBilling] = useState('monthly')

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-navy">Abonnement & Facturation</h1>
        <p className="text-sm text-gray-500 mt-1">
          Gérez votre plan Tapio et vos coordonnées bancaires
        </p>
      </div>

      {/* Current plan */}
      <Card className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-gold" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase">Niveau d'adhésion</p>
              <p className="text-xl font-bold text-navy">Tapio Gratuit</p>
            </div>
          </div>
          <Badge variant="gray">Plan actuel</Badge>
        </div>

        <p className="text-sm text-gray-600 mb-6">
          Votre compte gratuit inclut l'accès basique pour 1 profil individuel avec suivi métrique hebdomadaire.
        </p>

        <div className="grid md:grid-cols-3 gap-4">
          {[
            { label: 'Pages actives', value: '1/1', percent: 100, extra: 'Limite atteinte' },
            { label: 'Cartes appariées', value: '1/1', percent: 100, extra: '1 carte physique' },
            { label: 'Scans ce mois', value: '425/500', percent: 85, extra: 'Reset dans 9 jours' },
          ].map((item, i) => (
            <div key={i} className="bg-graysoft rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">{item.label}</p>
              <p className="text-lg font-bold text-navy mb-2">{item.value}</p>
              <div className="h-1.5 bg-white rounded-full overflow-hidden mb-2">
                <div
                  className={`h-full rounded-full ${item.percent >= 100 ? 'bg-red-500' : 'bg-teal'}`}
                  style={{ width: `${item.percent}%` }}
                />
              </div>
              <p className="text-xs text-gray-400">{item.extra}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Upgrade to Pro */}
      <Card className="p-6 border-2 border-teal/20">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs font-semibold text-teal uppercase">Passerelle Tapio Pro</span>
        </div>
        <h2 className="text-2xl font-bold text-navy mb-2">
          Passez au Plan Pro & débloquez tout le potentiel
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          Libérez vos contraintes. Déployez une infinité de microsites interactifs, menus digitaux et cartes pour l'ensemble de votre réseau.
        </p>

        {/* Billing toggle */}
        <div className="flex items-center gap-1 p-1 bg-graysoft rounded-xl mb-6 w-fit">
          <button
            onClick={() => setBilling('monthly')}
            className={`px-4 py-2 text-xs font-semibold rounded-lg transition-colors ${
              billing === 'monthly' ? 'bg-white shadow-sm text-navy' : 'text-gray-500'
            }`}
          >
            Mensuel
          </button>
          <button
            onClick={() => setBilling('yearly')}
            className={`px-4 py-2 text-xs font-semibold rounded-lg transition-colors flex items-center gap-2 ${
              billing === 'yearly' ? 'bg-white shadow-sm text-navy' : 'text-gray-500'
            }`}
          >
            Annuel
            <Badge variant="success" className="text-[10px]">-20%</Badge>
          </button>
        </div>

        <div className="bg-gradient-to-br from-navy to-navy-800 rounded-2xl p-6 text-white mb-6">
          <div className="flex items-end gap-2 mb-1">
            <span className="text-4xl font-bold">
              {billing === 'monthly' ? '19€' : '15€'}
            </span>
            <span className="text-sm opacity-70 mb-1.5">/ mois TTC</span>
          </div>
          <p className="text-xs opacity-70">
            {billing === 'monthly' ? 'Facturé mensuellement' : 'Facturé annuellement (180€/an)'}
          </p>

          <ul className="space-y-2 mt-6 text-sm">
            {[
              'Pages & profils illimités',
              'Domaine custom (nom, me ou dédié)',
              'Export instantané CSV des prospects & CRM',
              'Analytics complets géolocalisés',
              'QR codes customisés avec logo vectoriel',
              'Menu restaurant interactif dual € / MAD',
            ].map((feat, i) => (
              <li key={i} className="flex items-center gap-2">
                <Check className="w-4 h-4 text-teal flex-shrink-0" />
                {feat}
              </li>
            ))}
          </ul>

          <Button className="w-full mt-6" size="lg">
            Passer à Tapio Pro
          </Button>

          <p className="text-center text-xs opacity-60 mt-3">
            Activation instantanée • Sans engagement de durée
          </p>
        </div>
      </Card>

      {/* Payment method */}
      <Card className="p-6">
        <h2 className="font-bold text-navy mb-1">Moyens de paiement</h2>
        <p className="text-sm text-gray-500 mb-6">Synchronisé avec Stripe Gateway</p>

        <div className="bg-graysoft rounded-xl p-4 flex items-center gap-4">
          <div className="w-12 h-8 bg-navy rounded-md flex items-center justify-center">
            <span className="text-white text-xs font-bold">VISA</span>
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-navy">•••• •••• •••• 4242</p>
            <p className="text-xs text-gray-500">Alexandre Martin · 08/28</p>
          </div>
          <Button variant="outline" size="sm">Modifier</Button>
        </div>
      </Card>

      {/* Invoices */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-navy">Historique des factures</h2>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4" />
            Tout exporter (ZIP)
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-3 text-xs font-semibold text-gray-500">Date</th>
                <th className="text-left py-3 text-xs font-semibold text-gray-500">Référence</th>
                <th className="text-left py-3 text-xs font-semibold text-gray-500">Description</th>
                <th className="text-right py-3 text-xs font-semibold text-gray-500">Montant</th>
                <th className="text-center py-3 text-xs font-semibold text-gray-500">Statut</th>
              </tr>
            </thead>
            <tbody>
              {[
                { date: '15 Mai 2025', ref: 'INV-2025-005', desc: 'Abonnement Tapio Pro · Mensuel', amount: '19,00 €' },
                { date: '15 Avril 2025', ref: 'INV-2025-004', desc: 'Abonnement Tapio Pro · Mensuel', amount: '19,00 €' },
                { date: '15 Mars 2025', ref: 'INV-2025-003', desc: 'Abonnement Tapio Pro · Mensuel', amount: '19,00 €' },
                { date: '28 Fév 2025', ref: 'INV-2025-002', desc: 'Commande Matériel · 2x Cartes Bois', amount: '79,00 €' },
              ].map((inv, i) => (
                <tr key={i} className="border-b border-gray-50 hover:bg-graysoft">
                  <td className="py-3 text-gray-600">{inv.date}</td>
                  <td className="py-3 font-medium text-navy">{inv.ref}</td>
                  <td className="py-3 text-gray-600">{inv.desc}</td>
                  <td className="py-3 text-right font-semibold text-navy">{inv.amount}</td>
                  <td className="py-3 text-center">
                    <Badge variant="success" className="text-[10px]">Payée</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}